import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useAuth } from './auth';

export interface Dojo { id: string; name: string; short_name: string | null; region: string; town: string | null; website: string | null; is_honbu: boolean }
export interface Plan { id: string; code: string; name: string; category: string; price_pence: number; included_adults: number | null }
export interface Membership { id: string; starts_on: string; ends_on: string; status: string; plan: { name: string } | null }
export interface Person {
  id: string; first_name: string; last_name: string; email: string | null; date_of_birth: string | null;
  grade_rank: number | null; is_household_manager: boolean; user_id: string | null; household_id: string; member_code: string;
  dojo: { id: string; short_name: string | null; name: string } | null; memberships: Membership[];
  gradings: Grading[];
  photo_path: string | null; photo_consent: boolean; name_katakana: string | null; started_goju_ryu_on: string | null; started_karate_on: string | null; nationality: string | null; member_number: number | null;
  frozen_at: string | null; frozen_reason: string | null;
}
export interface Grading { id: string; graded_on: string; from_rank: number | null; for_rank: number; result: 'registered' | 'passed' | 'failed' | 'withdrawn'; recommended: boolean | null; recommended_by: string | null; recommendation_note: string | null; event: { id: string; title: string; slug: string } | null }
export interface EventOption { id: string; name: string; age_band: string | null; price_pence: number; sort_order: number; active?: boolean }
export interface EventRow {
  id: string; slug: string; title: string; type: string; description: string | null; starts_at: string; ends_at: string | null; venue: string | null;
  venue_address: string | null; contact_email: string | null; body_md: string | null;
  registration_closes_at: string | null; requires_active_membership: boolean; grading_offered: boolean; allow_pay_on_day: boolean; options: EventOption[];
}
const EVENT_COLUMNS = 'id,slug,title,type,description,body_md,starts_at,ends_at,venue,venue_address,contact_email,registration_closes_at,requires_active_membership,grading_offered,allow_pay_on_day,options:event_option(id,name,age_band,price_pence,sort_order,active)';

// No generated database types yet: results are cast to the shapes above, which match the select lists.
const unwrap = <T,>({ data, error }: { data: unknown; error: { message: string } | null }) => { if (error) throw new Error(error.message); return data as T; };

export const useDojos = () => useQuery({
  queryKey: ['dojos'],
  queryFn: async () => unwrap<Dojo[]>(await supabase.from('dojo').select('id,name,short_name,region,town,website,is_honbu').order('short_name')),
  staleTime: 60 * 60 * 1000,
});

export const usePlans = () => useQuery({
  queryKey: ['plans'],
  queryFn: async () => unwrap<Plan[]>(await supabase.from('membership_plan').select('id,code,name,category,price_pence,included_adults').order('price_pence')),
  staleTime: 60 * 60 * 1000,
});

export const useEvents = () => useQuery({
  queryKey: ['events'],
  queryFn: async () => unwrap<EventRow[]>(await supabase.from('event').select(EVENT_COLUMNS)
    .eq('published', true).gte('starts_at', new Date().toISOString()).order('starts_at')),
});

/** One published event by its address. Public: anyone can read it, signed in or not. */
export const useEventBySlug = (slug: string) => useQuery({
  queryKey: ['event', slug],
  queryFn: async () => (unwrap<EventRow[]>(await supabase.from('event').select(EVENT_COLUMNS).eq('slug', slug).eq('published', true)))[0] ?? null,
});

/** The signed-in person's household (RLS returns only their own people; instructors also see students, filtered out here). */
export function useHousehold() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['household', session?.user.id],
    enabled: !!session,
    queryFn: async () => {
      const me = unwrap<{ household_id: string }[]>(await supabase.from('person').select('household_id').eq('user_id', session!.user.id));
      if (!me.length) return null;
      const people = unwrap<Person[]>(await supabase.from('person')
        .select('id,first_name,last_name,email,date_of_birth,grade_rank,is_household_manager,user_id,household_id,member_code,photo_path,photo_consent,name_katakana,started_goju_ryu_on,started_karate_on,nationality,member_number,frozen_at,frozen_reason,dojo:home_dojo_id(id,short_name,name),memberships:membership(id,starts_on,ends_on,status,plan:plan_id(name)),gradings:grading!grading_person_id_fkey(id,graded_on,from_rank,for_rank,result,recommended,recommended_by,recommendation_note,event:event_id(id,title,slug))')
        .eq('household_id', me[0]!.household_id).eq('status', 'active').order('is_household_manager', { ascending: false }).order('date_of_birth'));
      // An active auto-renewal (Direct Debit) means the household need not renew by hand.
      const subs = unwrap<{ renews_on: string }[]>(await supabase.from('subscription').select('renews_on').eq('household_id', me[0]!.household_id).eq('status', 'active').order('renews_on').limit(1));
      return { householdId: me[0]!.household_id, people, autoRenewsOn: subs[0]?.renews_on ?? null };
    },
  });
}

/** The membership that counts today: active and not yet ended, else the most recent one. */
export function currentMembership(p: Person): Membership | null {
  const today = new Date().toISOString().slice(0, 10);
  const sorted = [...p.memberships].sort((a, b) => b.ends_on.localeCompare(a.ends_on));
  return sorted.find(m => m.status === 'active' && m.ends_on >= today) ?? sorted[0] ?? null;
}

/** The signed-in person's admin role: 'admin', 'viewer' (read-only admin), or null. RLS lets you read your own app_admin row. */
function useAdminRole() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['admin-role', session?.user.id],
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => (unwrap<{ role: 'admin' | 'viewer' }[]>(await supabase.from('app_admin').select('role').eq('user_id', session!.user.id))[0]?.role ?? null),
  });
}

/** Whether the signed-in person may open the admin area (an admin or an admin viewer). */
export function useIsAdmin() {
  const q = useAdminRole();
  return { isLoading: q.isLoading, data: q.data === undefined ? undefined : q.data !== null, isViewer: q.data === 'viewer' };
}

/** Whether the signed-in person may change things in the admin area. Admin viewers may not; the database refuses them too. */
export function useCanEdit() {
  return useAdminRole().data === 'admin';
}

export interface MyBooking { id: string; status: string; price_pence: number; pay_on_day: boolean; desk_note: string | null; person: { id: string; first_name: string } | null; option: { name: string } | null; event: { id: string; slug: string; title: string; starts_at: string; venue: string | null } | null }

/** Upcoming bookings for the signed-in person's household, and any earlier ones still to be paid. */
export function useMyBookings(householdId: string | undefined) {
  return useQuery({
    queryKey: ['my-bookings', householdId],
    enabled: !!householdId,
    queryFn: async () => {
      const rows = unwrap<(MyBooking & { person: { id: string; first_name: string; household_id: string } | null })[]>(await supabase.from('event_registration')
        .select('id,status,price_pence,pay_on_day,desk_note,person:person_id!inner(id,first_name,household_id),option:option_id(name),event:event_id(id,slug,title,starts_at,venue)')
        .eq('person.household_id', householdId!).in('status', ['pending', 'confirmed']));
      const now = new Date().toISOString();
      return rows.filter(r => r.event && (r.event.starts_at >= now || (r.status === 'pending' && r.pay_on_day))).sort((a, b) => a.event!.starts_at.localeCompare(b.event!.starts_at));
    },
  });
}

/** The instructor records for the signed-in person (RLS returns only their own). */
export function useMyInstructing() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ['my-instructing', session?.user.id],
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => unwrap<{ id: string; role: string; dbs_expires_on: string | null; insurance_expires_on: string | null; first_aid_expires_on: string | null; declaration_signed_on: string | null; dojo: { id: string; name: string; short_name: string | null } | null }[]>(
      await supabase.from('dojo_instructor').select('id,role,dbs_expires_on,insurance_expires_on,first_aid_expires_on,declaration_signed_on,dojo:dojo_id(id,name,short_name),person:person_id!inner(user_id)').eq('active', true).eq('person.user_id', session!.user.id)),
  });
}
